import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getChatLink, fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import { FiLink, FiUser } from "react-icons/fi";
import InfoTip from "../../utils/InfoTip";


const JobDetails = () => {
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState("");
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [descPopup, setDescPopup] = useState(false);
  const [messengerPopup, setMessengerPopup] = useState(false);
  const [messengerLinks, setMessengerLinks] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [messengerLoading, setMessengerLoading] = useState(false);

  const { jobId } = useParams();
  const navigate = useNavigate();

  const job = useSelector((state) => state.jobs.latestJob);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchJob = async () => {
      if (jobId) {
        setLocalLoading(true);
        await dispatch(fetchJobById(jobId));
        setLocalLoading(false);
      }
    };
    fetchJob();
  }, [dispatch, jobId]);

  // Loading is derived from Redux and local fetch: show spinner until fetch completes
  const loadingJob = localLoading || !job || job.id !== jobId;

  // Show every link that exists -- the generic one and the user's own
  // recruiter-tagged one. Drops empty links and duplicates.
  const uniqueLinks = (links) =>
    links.filter(
      (link, i) => link.url && links.findIndex((l) => l.url === link.url) === i
    );

  // linkValue drives the social share buttons: prefer the generic link.
  useEffect(() => {
    const url = job?.apply_url || job?.my_apply_url;
    if (url) {
      setLinkValue(url);
    }
  }, [job]);

  const handleCopyLink = () => {
    if (linkValue) {
      navigator.clipboard.writeText(linkValue);
      showSnack("Link copied to clipboard!", "success");
    }
  };

  const handleCopyMyApplyLink = () => {
    if (job?.my_apply_url) {
      navigator.clipboard.writeText(job.my_apply_url);
      showSnack("My URL copied to clipboard!", "success");
    }
  };

  const handleChatIconLink = () => {
    const links = uniqueLinks([
      { label: "Chat URL", url: job?.chat_url },
      { label: "My Chat URL", url: job?.my_chat_url },
    ]);
    if (!links.length) return;
    setMessengerLinks(links);
    setMessengerPopup(true);
  };

  const handleMessengerChatLink = async () => {
    if (!job?.id || !job?.title) return;
    setMessengerLoading(true);
    const result = await dispatch(getChatLink(job.id, job.title));
    setMessengerLoading(false);
    const toMessengerUrl = (url) =>
      url.replace("https://m.me/", `${window.location.origin}/messenger/`) + `/${job.id}`;
    const links = result?.success
      ? uniqueLinks([
          { label: "Messenger URL", url: result.chat_url },
          { label: "My Messenger URL", url: result.my_chat_url },
        ]).map((link) => ({ ...link, url: toMessengerUrl(link.url) }))
      : [];
    if (links.length) {
      setMessengerLinks(links);
      setMessengerPopup(true);
    } else {
      showSnack("Failed to get messenger chat link", "error");
    }
  };

  const handleCopyMessengerLink = (url, label) => {
    if (url) {
      navigator.clipboard.writeText(url);
      setMessengerPopup(false);
      showSnack(`${label} copied to clipboard!`, "success");
    }
  };

  const handleCopyDescription = () => {
    if (job?.description) {
      navigator.clipboard.writeText(job.description);
      showSnack("Description copied to clipboard!", "success");
    }
  };

  const handleShare = (platform) => {
    if (!linkValue) return;
    const url = encodeURIComponent(linkValue);
    let shareUrl;
    if (platform === "facebook") {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    } else if (platform === "linkedin") {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    }
    window.open(shareUrl, "_blank");
  };

  const snackClosed = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const showSnack = (message, severity = "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const shareLinks = [
    job?.apply_url && {
      key: "primary",
      variant: "public",
      title: "Job Level URL",
      badge: "Public job link",
      tip: "The general application link for this job. Share it anywhere. Candidates who apply through this link are assigned to recruiters using the job’s round-robin assignment method.",
      url: job.apply_url,
      onCopy: handleCopyLink,
      copyTitle: "Copy link",
    },
    job?.my_apply_url && job.my_apply_url !== job.apply_url && {
      key: "recruiter",
      variant: "personal",
      title: "Recruiter URL",
      badge: "Your personal link",
      tip: "Your personal application link. Candidates who apply through this link are automatically assigned to you as the recruiter.",
      url: job.my_apply_url,
      onCopy: handleCopyMyApplyLink,
      copyTitle: "Copy my link",
    },
  ].filter(Boolean);

  if (loadingJob) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!jobId) {
    return <div className="job-summary">Job ID is missing.</div>;
  }

  return (
    <div className="job-summary">
      {/* Job Title Section */}
      <div className="job-summary-title">
        <div className="job-links-head">
          <h5>Share Job Links</h5>
          <p>Use these links to invite candidates or share the job on external platforms.</p>
        </div>

        {/* Job link(s) and share options merged into one row */}
        <div className="job-link-card">
          {shareLinks.map((link) => (
            <div className="job-link-main" key={link.key}>
              <div className={`job-link-icon ${link.variant}`}>
                {link.variant === "personal" ? <FiUser /> : <FiLink />}
              </div>
              <div className="job-link-body">
                <div className="job-link-title-row">
                  <h6 style={{ display: "flex", alignItems: "center" }}>
                    {link.title}
                    <InfoTip text={link.tip} />
                  </h6>
                  <span className={`job-link-badge ${link.variant}`}>{link.badge}</span>
                </div>
                <div className="job-link-field">
                  <input type="text" value={link.url} readOnly title={link.url} />
                  <button className="job-link-copy-btn" onClick={link.onCopy} title={link.copyTitle}>
                    <img src="/images/copy-outline-icon.svg" alt="Copy" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Share Buttons */}
          <div className="job-link-share">
            <h6>Share on</h6>
            <div className="job-link-share-icons">
              <button className="share-btn" onClick={handleChatIconLink} title="Chat link">
                <img src="/images/chat-icon.svg" alt="Chat" width={26} height={26} />
              </button>
              {job?.enable_messenger && (
                <button
                  className="share-btn"
                  onClick={handleMessengerChatLink}
                  disabled={messengerLoading}
                  title="Messenger URL"
                >
                  {messengerLoading ? (
                    <div className="spinner-border" role="status" style={{ width: "18px", height: "18px", borderWidth: "2px" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <img src="/images/facebook-messenger.svg" alt="Messenger" width={24} height={24} />
                  )}
                </button>
              )}
              <button className="share-btn" onClick={() => handleShare("facebook")} title="Facebook">
                <img src="/images/Facebook-icon.svg" alt="Facebook" width={24} height={24} />
              </button>
              <button className="share-btn" onClick={() => handleShare("linkedin")} title="LinkedIn">
                <img src="/images/LinkedIn-icon.svg" alt="LinkedIn" width={30} height={30} />
              </button>
            </div>
          </div>
        </div>

      </div>
      {/* Job Details */}
      <div className="job-candidte-details">
        <div className="job-details">
          <div className="job-details-item">
            <div className="job-details-basic">
              <div className="detail-item">
                <h6>Company</h6>
                <p>{job?.company_name || "null"}</p>
              </div>
              <div className="detail-item">
                <h6>Employment type</h6>
                <p>{job?.job_type?.name || "null"}</p>
              </div>
              <div className="detail-item">
                <h6>Salary range</h6>
                <p>{job?.salary || "null"}</p>
              </div>
              <div className="detail-item">
                <h6>Location</h6>
                <p>
                  <span>Country : {job?.country?.name || null}</span>,<br />
                  <span>City : {job?.city?.name || job?.cities?.map((city) => {return city;}).join(", ") || "null"}</span>
                </p>
              </div>
              <div className="detail-item">
                <h6>Experience</h6>
                <p>{job?.experience_required || "null"}</p>
              </div>
              <div className="detail-item">
                <h6>Skills</h6>
                <div className="multiple-skills">
                  {job?.skills?.slice(0, 2).map((skills, index) => (
                    <p className="candidate-skills" key={index}>
                      {skills}
                    </p>
                  ))}
                  {job?.skills?.length > 2 && (
                    <div className="candidate-skills more-skills">
                      +{job.skills.length - 2} more
                      <div className="skills-popup">
                        {job.skills.slice(2).map((s, idx, arr) => (
                          <span key={idx}>
                            {s}
                            {idx < arr.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {job?.job_tags?.length > 0 && (
              <div className="detail-item">
                <h6>Tags</h6>
                <div className="multiple-skills">
                  {job?.job_tags?.slice(0, 2).map((tags, index) => (
                    <p className="candidate-skills" key={index}>
                      {tags}
                    </p>
                  ))}
                  {job?.job_tags?.length > 2 && (
                    <div className="candidate-skills more-skills">
                      +{job.job_tags.length - 2} more
                      <div className="skills-popup">
                        {job.job_tags.slice(2).map((t, idx, arr) => (
                          <span key={idx}>
                            {t}
                            {idx < arr.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
            <div className="detail-item">
              <div className="d-flex justify-content-between">
                <h6>Description</h6>
                <button className="job-description-inputbtn" onClick={handleCopyDescription} title="Copy description">
                  <img src="/images/copy-outline-icon.svg" alt="" />
                </button>
              </div>
              <p style={{ lineHeight: '28px', whiteSpace: "pre-line" }}>
                {job?.description || "null"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showVoicePopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="d-flex justify-content-between align-items-center">
              <h3>Set up voice interview</h3>
              <img
                src="/images/close.svg"
                alt=""
                onClick={() => setShowVoicePopup(false)}
              />
            </div>
            <div className="job-form">
              <div className="job-form-inputs d-flex">
                <div className="form-section">
                  <label>Select voice</label>
                  <input type="text" placeholder="input text" />
                </div>
                <div className="form-section">
                  <label>Call hours</label>
                  <input type="text" placeholder="input text" />
                </div>
              </div>
              <div className="job-form-inputs d-flex">
                <div className="form-section">
                  <label>Number of attempts (each candidate)</label>
                  <input type="number" placeholder="input text" />
                </div>
                <div className="form-section">
                  <label>Language</label>
                  <input type="text" placeholder="input text" />
                </div>
              </div>
              <button
                className="submit-btn"
                onClick={() => navigate(`/jobs/${jobId}/candidates`)}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {descPopup && (
        <div className="upload-modal">
          <div
            className="modal-content"
            style={{ width: "600px", height: "auto" }}
          >
            <div className="modal-head">
              <h3>Job Details</h3>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => setDescPopup(false)}
              />
            </div>
            <div
              className="modal-input d-flex flex-column align-items-start"
              style={{ height: "220px", width: "auto", padding: "0" }}
            >
              <textarea
                value={job?.description}
                placeholder="Enter description"
                style={{
                  height: "220px",
                  background: "transparent",
                  borderRadius: "8px",
                  border: "none",
                }}
              />
            </div>
            <div className="d-flex justify-content-center">
              <button
                className="upload-btn"
                onClick={() => setDescPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {messengerPopup && (
        <div className="upload-modal">
          <div className="modal-content" style={{ width: "500px", height: "auto" }}>
            <div className="modal-head">
              <h3>Messenger Chat Link</h3>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => setMessengerPopup(false)}
              />
            </div>
            <div style={{ padding: "20px 24px 28px", display: "flex", flexDirection: "column", gap: "16px" }}>
              {messengerLinks.map(({ label, url }) => (
              <div key={label}>
              {messengerLinks.length > 1 && (
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#344054", marginBottom: "6px" }}>
                  {label}
                </label>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="text"
                  value={url}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #d0d5dd",
                    fontSize: "14px",
                    color: "#344054",
                    background: "#f9fafb",
                    outline: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                />
                <button
                  onClick={() => handleCopyMessengerLink(url, label)}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: "1px solid #f75891",
                    background: "transparent",
                    color: "#000",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Copy URL
                </button>
              </div>
              </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default JobDetails;
